// Aviation / topo / imagery tile sources used by Fyrefly.
//
// The basemap stack is a TWO-tier model (mirrors ForeFlight):
//
//   1. BASE IMAGERY  (mutually exclusive, always one):
//        basic     — light OSM streets (free, low data)
//        street    — full-color OSM streets
//        satellite — Esri World Imagery (incl. ocean bathymetry)
//
//   2. CHART OVERLAY (mutually exclusive, optional):
//        none      — no aeronautical chart raster
//        sectional — VFR Sectional
//        ifr-low   — IFR Enroute Low
//        ifr-high  — IFR Enroute High
//        helo      — Helicopter Route Chart
//
// FAA charts are opaque JPGs but only have tiles within their published
// coverage. Outside coverage (Pacific Ocean, Mexico, etc.) the proxy
// returns 404, MapLibre treats it as transparent, and the imagery base
// shows through. That's the "satellite under the sectional" effect
// boss is used to in ForeFlight.
//
// All sources are routed through Fyrefly's /api/tile edge proxy for CORS,
// edge caching, and the freedom to swap upstreams server-side without
// redeploying clients.

export type BaseImageryId = 'basic' | 'street' | 'satellite' | 'topo';
export type ChartOverlayId = 'none' | 'sectional' | 'ifr-low' | 'ifr-high' | 'helo';

export interface BaseImageryDef {
  id: BaseImageryId;
  name: string;
  attribution: string;
  /** Tile URL templates. Empty for `basic`/`street` when we use direct OSM. */
  tiles: string[];
  tileSize?: number;
  minzoom?: number;
  maxzoom?: number;
}

export interface ChartOverlayDef {
  id: ChartOverlayId;
  name: string;
  attribution: string;
  tiles: string[];
  tileSize?: number;
  minzoom?: number;
  maxzoom?: number;
}

function proxy(layer: string): string {
  return `/api/tile?layer=${layer}&z={z}&x={x}&y={y}`;
}

// FAA chart tiles get wrapped in the vfrchart:// custom protocol so
// the client-side grey-pixel transparency pass can run on them before
// MapLibre uploads them to the GPU. See chartTileTransparency.ts for
// the rationale and pixel kernel.
function chartProxy(layer: string): string {
  return `vfrchart://${proxy(layer)}`;
}

export const BASE_IMAGERY: Record<BaseImageryId, BaseImageryDef> = {
  basic: {
    id: 'basic',
    // "Basic" is a low-saturation OSM rendering. We dim it in the style
    // so chart overlays read better on top.
    name: 'Basic',
    attribution: '© OpenStreetMap contributors',
    tiles: [
      'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
    ],
    tileSize: 256,
    minzoom: 0,
    maxzoom: 19,
  },
  street: {
    id: 'street',
    name: 'Street',
    attribution: '© OpenStreetMap contributors',
    tiles: [
      'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
    ],
    tileSize: 256,
    minzoom: 0,
    maxzoom: 19,
  },
  satellite: {
    id: 'satellite',
    name: 'Satellite',
    // Esri attribution required by their terms.
    attribution:
      'Imagery © Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    tiles: [proxy('satellite')],
    tileSize: 256,
    minzoom: 0,
    maxzoom: 19,
  },
  topo: {
    id: 'topo',
    name: 'Topo',
    // USGS Topo composite — hillshade + named features + elevation
    // contours. Useful when zoomed in for terrain-aware planning,
    // especially for helo / fire-aviation work where reading the
    // ridgelines under the chart matters.
    attribution: 'Map services © U.S. Geological Survey',
    tiles: [proxy('topo')],
    tileSize: 256,
    minzoom: 0,
    maxzoom: 16,
  },
};

export const CHART_OVERLAYS: Record<Exclude<ChartOverlayId, 'none'>, ChartOverlayDef> = {
  sectional: {
    id: 'sectional',
    name: 'VFR Sectional',
    attribution: '© FAA Aeronautical Information Services',
    tiles: [chartProxy('sectional')],
    tileSize: 256,
    minzoom: 3,
    maxzoom: 12,
  },
  helo: {
    id: 'helo',
    name: 'Helicopter',
    attribution: '© FAA Aeronautical Information Services',
    tiles: [chartProxy('helo')],
    tileSize: 256,
    minzoom: 3,
    maxzoom: 13,
  },
  'ifr-low': {
    id: 'ifr-low',
    name: 'IFR Low',
    attribution: '© FAA Aeronautical Information Services',
    tiles: [chartProxy('ifr-low')],
    tileSize: 256,
    minzoom: 3,
    maxzoom: 11,
  },
  'ifr-high': {
    id: 'ifr-high',
    name: 'IFR High',
    attribution: '© FAA Aeronautical Information Services',
    tiles: [chartProxy('ifr-high')],
    tileSize: 256,
    minzoom: 3,
    maxzoom: 10,
  },
};

// ---- Back-compat shims for callers still on the old single-base model.
// Old code imported BaseLayerId / BASE_LAYERS. Keep the type exported so
// the migration is one PR, not five.

export type BaseLayerId =
  | BaseImageryId
  | Exclude<ChartOverlayId, 'none'>
  | 'topo'
  | 'osm';

// NIFC current wildfire perimeters (WFIGS Interagency, public).
// We pull as GeoJSON via the ArcGIS query endpoint with f=geojson.
export const NIFC_PERIMETERS_GEOJSON =
  'https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/WFIGS_Interagency_Perimeters_Current/FeatureServer/0/query' +
  '?where=1%3D1&outFields=poly_IncidentName,attr_FireDiscoveryDateTime,attr_IncidentSize,attr_IncidentTypeCategory&outSR=4326&f=geojson';
